import { useStyletron } from '@thanx/uikit/theme'
import {
  type MenuErrorSummary,
  useGetMenuErrorDetailQuery,
} from 'api/menuErrors'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useMemo, useState } from 'react'
import ErrorRow from './ErrorRow'
import groupOccurrences from './groupOccurrences'
import LocationsModal from './LocationsModal'

const t = buildTranslate('pos_configurations.menu_errors_list')

// Badge palette intentionally matches the row severity icons so the
// header counts and the inline icons read as the same tier at a glance;
// Healthy mirrors the Locations list's green chip (nephritis pair) so a
// zero-error menu reads the same as a zero-error location.
enum BadgeTone {
  Errors = 'errors',
  Notices = 'notices',
  Healthy = 'healthy',
}
// `cayenne` (unsuffixed) is the canonical darkest shade in the family
// — pairing it with the 10 tint hits Figma's intended badge contrast
// without inventing a new palette token.
const BADGE_STYLES: Record<BadgeTone, { background: string; color: string }> = {
  [BadgeTone.Errors]: { background: palette.cayenne10, color: palette.cayenne },
  [BadgeTone.Notices]: {
    background: palette.eggYolk10,
    color: palette.eggYolk,
  },
  [BadgeTone.Healthy]: {
    background: palette.nephritis20,
    color: palette.nephritis,
  },
}
const badgeStyle = (tone: BadgeTone) => BADGE_STYLES[tone]

const Badge = ({ tone, count }: { tone: BadgeTone; count: number }) => {
  const [css] = useStyletron()
  const style = badgeStyle(tone)
  const label = t(`badge.${tone}`, { count }) as string
  return (
    <span
      className={`bold ${css({
        backgroundColor: style.background,
        color: style.color,
        fontSize: '11px',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        padding: '4px 8px',
        borderRadius: '4px',
        whiteSpace: 'nowrap',
      })}`}
    >
      {label}
    </span>
  )
}

type Props = {
  menu: MenuErrorSummary
}

const MenuCard = ({ menu }: Props) => {
  const [css] = useStyletron()
  const [modalOpen, setModalOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  // Lazy-load: the per-menu detail endpoint is only hit once the operator
  // expands the card (or opens the locations modal, which also reads the
  // detail payload). A merchant can have 100+ menus, so firing every
  // card's detail query on mount was an N+1 storm; the summary badges
  // (from the list payload) give the at-a-glance counts without it.
  //
  // Expand and modal-open share this one query (and its RTK Query cache):
  // opening the modal first populates the cache, so a later expand renders
  // its error rows from cache without re-fetching. That's intentional — the
  // gate is "fetch once either trigger fires", not "fetch per trigger" — so
  // seeing rows appear instantly after a modal visit is not a lazy-load bug.
  const { data, isLoading, isError } = useGetMenuErrorDetailQuery(
    { menuName: menu.name },
    { skip: !expanded && !modalOpen }
  )

  // Render rows in the same order as the BE classifier (errors first, then
  // notices) so the most-actionable rows land closest to the card header,
  // with same-reason occurrences collapsed into one row of merged items
  // (thirty per-item "missing image" strings → one row, thirty items).
  const occurrences = useMemo(
    () => groupOccurrences([...(data?.errors ?? []), ...(data?.notices ?? [])]),
    [data]
  )

  const isHealthy =
    menu.configErrorsCount.error === 0 && menu.configErrorsCount.notice === 0

  // A healthy card expands to a "no errors" body, so the toggle must not
  // announce "Show/Hide errors" — that promises a purpose the revealed
  // content contradicts (WCAG 2.5.3 Label in Name). Use status-framed copy
  // for healthy cards, error-framed copy when there's something to fix.
  const toggleLabel = (
    expanded
      ? t(isHealthy ? 'hide_card_aria' : 'hide_errors_aria', {
          menuName: menu.name,
        })
      : t(isHealthy ? 'show_card_aria' : 'show_errors_aria', {
          menuName: menu.name,
        })
  ) as string

  return (
    <div
      className={`mb-l ${css({
        backgroundColor: palette.white,
        border: `1px solid ${palette.grey20}`,
        borderRadius: '4px',
        overflow: 'hidden',
      })}`}
    >
      <div className="d-flex align-items-start justify-content-between p-l">
        <div className="flex-grow-1 pr-m">
          <h3
            className={`bold mb-xxs ${css({
              color: palette.grey90,
              fontSize: '18px',
              lineHeight: '24px',
              wordBreak: 'break-word',
            })}`}
          >
            {menu.name}
          </h3>
          <div className={`d-flex align-items-center ${css({ gap: '8px' })}`}>
            <span className={`body-text-4 ${css({ color: palette.grey70 })}`}>
              {t('used_in_locations', { count: menu.locationCount }) as string}
            </span>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              aria-label={
                t('view_locations_aria', { menuName: menu.name }) as string
              }
              // No project-wide unstyled-button utility exists; reset
              // the default browser chrome inline so the link reads as
              // a text link rather than a button.
              className={`body-text-4 ${css({
                appearance: 'none',
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: palette.belizeHole,
                cursor: 'pointer',
                textDecoration: 'underline',
                ':hover': { textDecoration: 'none' },
                ':focus-visible': {
                  outline: `2px solid ${palette.belizeHole}`,
                  outlineOffset: '2px',
                },
              })}`}
            >
              {t('view_locations') as string}
            </button>
          </div>
        </div>
        <div className={`d-flex align-items-center ${css({ gap: '8px' })}`}>
          {menu.configErrorsCount.error > 0 && (
            <Badge
              tone={BadgeTone.Errors}
              count={menu.configErrorsCount.error}
            />
          )}
          {menu.configErrorsCount.notice > 0 && (
            <Badge
              tone={BadgeTone.Notices}
              count={menu.configErrorsCount.notice}
            />
          )}
          {isHealthy && <Badge tone={BadgeTone.Healthy} count={0} />}
          <button
            type="button"
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            aria-label={toggleLabel}
            // No project-wide unstyled-button utility exists; reset the
            // default chrome inline so the chevron reads as an affordance
            // on the card header rather than a nested card.
            className={css({
              appearance: 'none',
              background: 'transparent',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              color: palette.grey50,
              lineHeight: 1,
              ':focus-visible': {
                outline: `2px solid ${palette.belizeHole}`,
                outlineOffset: '2px',
              },
            })}
          >
            <i
              className={`fa fa-chevron-${expanded ? 'up' : 'down'} ${css({
                fontSize: '14px',
              })}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      {expanded && isLoading && (
        <p
          role="status"
          className={`body-text-4 mb-0 px-l py-m ${css({
            color: palette.grey70,
            borderTop: `1px solid ${palette.grey20}`,
          })}`}
        >
          {t('card_loading') as string}
        </p>
      )}
      {expanded && isError && (
        <p
          role="alert"
          className={`body-text-4 mb-0 px-l py-m ${css({
            color: palette.cayenne,
            borderTop: `1px solid ${palette.grey20}`,
          })}`}
        >
          {t('card_error') as string}
        </p>
      )}
      {expanded && data && occurrences.length === 0 && (
        <p
          className={`body-text-4 mb-0 px-l py-m ${css({
            color: palette.grey70,
            borderTop: `1px solid ${palette.grey20}`,
          })}`}
        >
          {t('card_healthy') as string}
        </p>
      )}
      {expanded &&
        data &&
        occurrences.map((occurrence, index) => (
          // Key on the row's display identity (the grouped reason when
          // present) rather than the raw wire message — a grouped row's
          // anchor message is whichever same-reason occurrence arrived
          // first, which can change across refetches. The index
          // disambiguates repeats.
          <ErrorRow
            key={`${occurrence.severity}-${
              occurrence.description ?? occurrence.message
            }-${index}`}
            occurrence={occurrence}
          />
        ))}
      <LocationsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        menuName={menu.name}
        locations={data?.locations ?? []}
        isLoading={isLoading}
        isError={isError}
      />
    </div>
  )
}

export default MenuCard
